import { generatePath } from 'react-router-dom'
import groupBy from 'lodash/fp/groupBy'

import * as Actions from 'redux/actions'
import client from 'api/client'
import store from 'redux/store'
import { Auth, CompanyDetail, Workspace, SuperAdminMode, UserRole } from 'types'
import { History } from 'history'
import { WORKSPACE_PATH } from 'paths'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { ALMANAC_CORE_WORKSPACE_ID, APP_HELPCENTER_WORKSPACE_ID, BLAZE_HELPCENTER_WORKSPACE_ID } from './constants'
import { DateTime } from 'luxon'
import { useQueryParams } from 'hooks/useQueryParams'
import { WORKSPACE_INVITE_TOKEN_PARAM } from 'screens/LoginOrSignup/constants'
import { getLocationUrl } from 'hooks/useHistory'

export const WORKSPACE_INVITE_KEY = 'workspace-invite'
export const URL_PARAMS_KEY = 'signup-analytics-params'

export const setWorkspaceInviteToken = (token: string) => {
  localStorage.setItem(WORKSPACE_INVITE_KEY, token)
}

export const removeWorkspaceInviteToken = () => {
  localStorage.removeItem(WORKSPACE_INVITE_KEY)
}

export const useWorkspaceInviteToken = () => {
  const queryParams = useQueryParams()
  return queryParams[WORKSPACE_INVITE_TOKEN_PARAM] || localStorage.getItem(WORKSPACE_INVITE_KEY) || ''
}

export const setUrlParams = (params: string) => {
  localStorage.setItem(URL_PARAMS_KEY, params)
}

export const getUrlParams = () => {
  const params = localStorage.getItem(URL_PARAMS_KEY)
  if (params) {
    return params
  }
}

export const filterPrivateWorkspaces = (workspaces: Workspace[]) => {
  return workspaces.filter((ws) => ws.id !== ALMANAC_CORE_WORKSPACE_ID && ws.role !== UserRole.GUEST)
}

export const addUserToInvitedWorkspace = async (
  auth: Auth,
  history: History,
  workspaceInviteToken: string,
  completeSignup: (auth: Auth) => void,
) => {
  if (!workspaceInviteToken || !auth) return

  const res = await WorkspaceClient.getInvitedWorkspace(workspaceInviteToken)

  if (res.type === 'success') {
    let workspaceInviteKey = res.data.company.key
    let workspaceInviteId = res.data.company.id

    const response = await client.addMember({
      email: auth.email,
      role: 'User',
      workspaceKey: workspaceInviteKey,
      message: '',
      url_invitation: true,
    })

    if (response.type === 'success') {
      store.dispatch(Actions.currentWorkspaceIdSet(workspaceInviteId))
      const workspacesResp = await client.getUserWorkspaces()
      if (workspacesResp.type === 'success') {
        store.dispatch(Actions.workspacesSet(workspacesResp.data.workspaces))
        removeWorkspaceInviteToken()
        completeSignup(auth)
        history.push(generatePath(WORKSPACE_PATH, { workspaceId: workspaceInviteId }))
      }
    }
  }
}

export const isAlmanacCoreWorkspace = (workspaceId: Workspace['id']) => workspaceId === ALMANAC_CORE_WORKSPACE_ID
export const isHelpCenterWorkspace = (workspaceId: Workspace['id']) =>
  [APP_HELPCENTER_WORKSPACE_ID, BLAZE_HELPCENTER_WORKSPACE_ID].includes(workspaceId) ||
  ['helpcenter.almanac.io', 'blazehelpcenter.almanac.io'].includes(getLocationUrl().host)

type GroupableWorkspace = Pick<Workspace, 'role' | 'id'>
type WorkspaceGroup = 'guest' | 'member' | 'almanac'

const getWorkspaceGroup = (workspace: GroupableWorkspace): WorkspaceGroup => {
  if (isAlmanacCoreWorkspace(workspace.id)) return 'almanac'
  return workspace.role === UserRole.GUEST ? 'guest' : 'member'
}

export const nonDefaultWorkspaces = (workspaces?: Workspace[]) => {
  return workspaces?.filter((ws: Workspace) => ws.name !== 'My Workspace' && !isAlmanacCoreWorkspace(ws.id))
}

export const findPrivateWorkspaces = (workspaces?: Workspace[]) => {
  if (!workspaces) return []
  return workspaces?.filter((ws: Workspace) => !isAlmanacCoreWorkspace(ws.id))
}

export const createPrivateWorkspace = async (name = 'My Workspace') => {
  return await client.postUserCompany(name)
}
/**
 * Accepts a list of workspaces and groups them into three groups:
 *  1. `almanac` - if the workspace id matches `ALMANAC_CORE_WORKSPACE_ID`
 *  2. `guest` - if the `guest` field is `true`
 *  3. `member` - if the `guest` field is `false`
 */
export const groupWorkspacesByType = groupBy(getWorkspaceGroup) as <T extends GroupableWorkspace>(
  workspaces: T[],
) => Partial<Record<WorkspaceGroup, T[]>>

export const detectPlatform = () => {
  const ua = window.navigator.userAgent
  if (ua.match(/\b(iPhone|iPad|iPod|iPod touch);/) || ua.includes('MobileSafari')) {
    return 'ios'
  }
  if (ua.match(/\bAndroid\b/)) {
    return 'android'
  }
  return 'desktop'
}

/**
 * converts type CompanyDetail (often returned from a request) to the more generally used Workspace Type.
 * @param company: CompanyDetail
 * @returns workspace: Workspace
 */
export const companyDetailToWorkspace = (company: CompanyDetail): Workspace => {
  return {
    id: company.id,
    created_at: DateTime.utc().toISO(),
    name: company.name,
    key: company.key,
    logo_url: company.logo_url,
    unpaid_articles_count: 0,
    guest_articles_count: 0,
    admin: true,
    super_admin: true,
    paying: false,
    invitation_token: company.invitation_token,
    invitation_token_enabled: company.invitation_token_enabled,
    invitation_token_expiration_days: company.invitation_token_expiration_days,
    show_portal: false,
    show_make_public: false,
    guest: false,
    is_new: false,
    homepage_doc_slug: null,
    homepage_doc: null,
    role: UserRole.MEMBER,
    show_all_handbooks: false,
    snippets_enabled: false,
    handbook_default_read_mode: false,
    workspace_domain: null,
    members_count: 1,
    payment_failed: false,
    auto_membership_domains: [],
    features: [],
    version_control_mode: {
      value: false,
      metadata: null,
    },
    allow_public_links: true,
    documentation_enabled: true,
    layers_enabled: true,
    can_start_free_trial: false,
    color_scheme: null,
    app_logo_url: null,
    image_library_project: company.image_library_project,
    is_virtual_marketer: false,
    is_virtual_marketer_onboarding_completed: false,
    virtual_marketer_onboarding_completed_at: null,
    show_virtual_marketer_upgrade: false,
    pause_autopilot_generate_at: null,
    pause_autopilot_publish_at: null,
    resume_autopilot_generate_at: null,
    resume_autopilot_publish_at: null,
    post_approval_enabled: false,
    is_ap_freemium: false,
    is_ap_freemium_brand_plan_set: false,
    is_campaigns: false,
    is_whitelabel: false,
    white_label_color: null,
    auto_reload_credits: false,
    auto_reload_limit: null,
    is_ads_testing_enabled: false,
  }
}

export const inSuperAdminMode = (workspaceId: number, auth: SuperAdminMode | null) => {
  return !!auth && auth.superAdminMode === workspaceId
}

/**
 * Determines the user's role based on workspace and authentication status.
 * This is a simplified version of the logic used in components that need
 * to determine if a user is a workspace member, guest, or unauthenticated.
 *
 * @param workspace - The workspace object (may be undefined)
 * @param auth - The authentication object (may be null)
 * @returns UserRole - The determined user role
 */
export const getUserRole = (workspace: { role?: UserRole } | undefined, auth: any): UserRole => {
  if (workspace?.role) {
    return workspace.role
  }
  if (auth) {
    return UserRole.GUEST
  }
  return UserRole.UNAUTHED
}
